package com.dotpx.ambient

import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.exp
import kotlin.math.max
import kotlin.math.min
import kotlin.math.sin

/** Filtered material voices and their strike, call, and pass-by envelopes. */
internal class MaterialEventState(private val sampleRate: Double) {
    private var materialNoiseFast = 0f
    private var materialNoiseLow = 0f
    private val materialFastAlpha = (1.0 - exp(-2.0 * PI * min(3_800.0, sampleRate * 0.45) / sampleRate)).toFloat()
    private val materialLowAlpha = (1.0 - exp(-2.0 * PI * min(260.0, sampleRate * 0.45) / sampleRate)).toFloat()

    fun reset() {
        materialNoiseFast = 0f
        materialNoiseLow = 0f
    }

    fun sample(kind: EventKind, noise: Float, primary: Float, secondary: Float, time: Double): Float {
        materialNoiseFast += materialFastAlpha * (noise - materialNoiseFast)
        materialNoiseLow += materialLowAlpha * (noise - materialNoiseLow)
        val band = materialNoiseFast - materialNoiseLow
        return when (kind) {
            EventKind.WindChime -> primary * 0.70f + secondary * exp(-time * 2.2).toFloat() * 0.30f
            EventKind.BuoyBell -> primary * 0.72f + secondary * exp(-time * 0.7).toFloat() * 0.28f
            EventKind.Owl -> primary * 0.94f + secondary * 0.04f + materialNoiseLow * 0.08f
            EventKind.Cricket -> (primary * 0.80f + secondary * 0.12f) *
                max(0.0, sin(time * PI * 2.0 * 29.0)).toFloat()
            EventKind.Bubble -> primary * 0.80f + secondary * 0.08f + band * 0.12f
            EventKind.FootstepGravel -> band * (0.70f + abs(noise) * 0.60f) + materialNoiseLow * 0.65f
            EventKind.FootstepSnow -> materialNoiseLow * 1.5f + band * (0.20f + abs(noise) * 0.35f)
            EventKind.BranchSnap -> band * 1.1f + primary * 0.12f
            // Two soft splashes with a short water resonance and a damped leaf rustle.
            EventKind.LeafDrip -> materialNoiseLow * 0.85f + band * 0.18f +
                primary * exp(-time * 8.0).toFloat() * 0.22f
            // Filtered tire wash and a subdued freewheel texture; no bell or sharp clicks.
            EventKind.BicyclePass -> {
                val spokes = (0.5 + 0.5 * sin(time * PI * 2.0 * 17.0)).toFloat()
                materialNoiseLow * 1.5f + band * (0.055f + spokes * 0.035f)
            }
            EventKind.ClockTick -> band * 0.55f + primary * 0.30f + secondary * 0.15f
            else -> 0f
        }
    }

    fun envelope(kind: EventKind, progress: Double, totalFrames: Int): Float {
        val duration = totalFrames.toDouble() / sampleRate
        val time = progress * duration
        val remaining = (1.0 - progress) * duration
        val envelope = when (kind) {
            EventKind.WindChime -> strike(time, 0.0, 0.004, 0.90)
            EventKind.BuoyBell -> strike(time, 0.0, 0.008, 1.40)
            EventKind.LeafDrip -> strike(time, 0.0, 0.012, 0.085) +
                strike(time, duration * 0.32, 0.020, 0.12) * 0.55
            EventKind.BicyclePass -> {
                val passing = sin(PI * progress)
                passing * passing * passing
            }
            EventKind.Owl -> hoot(time, 0.0, duration * 0.27) * 0.65 +
                hoot(time, duration * 0.40, duration * 0.45)
            EventKind.Cricket -> {
                val phrase = max(0.0, sin(PI * progress * 3.0))
                phrase * phrase
            }
            EventKind.Bubble -> strike(time, 0.0, 0.006, 0.055)
            EventKind.FootstepGravel -> strike(time, 0.0, 0.018, 0.12) +
                strike(time, duration * 0.48, 0.018, 0.14) * 0.85
            EventKind.FootstepSnow -> strike(time, 0.0, 0.050, 0.18) +
                strike(time, duration * 0.48, 0.060, 0.20) * 0.90
            EventKind.BranchSnap -> strike(time, 0.0, 0.001, 0.014) +
                strike(time, duration * 0.16, 0.001, 0.030) * 0.60
            EventKind.ClockTick -> strike(time, 0.0, 0.001, 0.007) +
                strike(time, 0.055, 0.001, 0.011) * 0.55
            else -> 0.0
        }
        return (min(envelope, 1.0) * min(remaining / 0.040, 1.0)).toFloat()
    }

    private fun strike(time: Double, start: Double, attack: Double, decay: Double): Double {
        val elapsed = time - start
        if (elapsed <= 0.0) return 0.0
        return min(elapsed / attack, 1.0) * exp(-elapsed / decay)
    }

    private fun hoot(time: Double, start: Double, duration: Double): Double {
        val progress = (time - start) / duration
        if (progress <= 0.0 || progress >= 1.0) return 0.0
        val shape = sin(PI * progress)
        return shape * shape
    }

}
